import { useContext } from 'react';
import { t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { FormGroup, Table } from 'src/components/ui';
import Loader from '../Loader';
import Notification from '../Notification';
import { EmptyState } from '../Icons/EmptyStates';
import useResourceLoader from '../../util/useResourceLoader';
import UWEEntityApi from '../../api/UWEEntityApi';
import { AuthContext } from '../../context/AuthContext';
import dayjs from 'dayjs';

interface Amendment {
  id: number;
  createdAt: string;
  canWork?: boolean;
  metadata?: {
    versionNumber?: number;
    current?: 'Original' | 'Actual' | 'Previa';
  };
  status?: { name?: string }[];
}

interface OcifReportAmendmentsListProps {
  schema?: { title?: string };
  registry?: { formContext?: { entityId?: number | string } };
  fieldPathId?: { $id?: string };
}

const amendmentRoute = (id: number | string) => `/workflow/ocfi-report-amendment/${id}`;

// The label shown for an amendment's stage is a distinct, business-facing name, not the raw
// workflow stage the backend stores in `status[0].name` — this is what used to be a jsonata
// `valueTranform` on this column in the report's schema. Values match the same canonical status
// vocabulary the amendment forms' status bars use (see AMENDMENT_STATUS_LABELS in
// util/markdownTemplate.ts) — that lookup keys off the raw workflowStep id, this one off the
// step's title, since that's the only thing `status[0].name` gives us here.
function amendmentStatusLabel(name?: string): string {
  switch (name) {
    case 'Report Review':
      return t`Under OCIF review`;
    case 'Reports and Data to Amend':
    case 'Report and Data to Amend':
      return t`Amendment draft`;
    case 'Amend Data':
      return t`Pending institution response`;
    case 'Completed':
      return t`Completed`;
    default:
      return '---';
  }
}

// Lists a report's amendment history (used only on the FFEIC report page, as the "Versions" field).
// A real WorkTray fetches its own lookup params asynchronously from formContext, which is what
// caused requests to sometimes go out with the report id filter missing (see git history on
// WorktrayEntitySelectField, the generic field this replaced). This field already knows exactly
// which entity it needs — the report being viewed — so it fetches directly with that id, no
// generic expr/jsonata lookup layer involved.
function OcifReportAmendmentsList(props: OcifReportAmendmentsListProps) {
  const { i18n } = useLingui();
  const { schema: { title } = {}, registry, fieldPathId: { $id } = {} } = props;
  const entityId = registry?.formContext?.entityId;
  const auth = useContext(AuthContext) as any;
  const orgUnitId = auth?.user?.user?.actualOrganizationalUnitId;

  const [rows, resourceLoading = true, error] = useResourceLoader<Amendment[]>(async () => {
    // Wait for the report's own id — never fetch (and never show every institution's amendments)
    // with the report filter missing.
    if (!entityId || !orgUnitId) return useResourceLoader.DoNotSet;

    const data = await UWEEntityApi.getWorkTray({
      resource: `uwe-entities/Amendment/list/${orgUnitId}`,
      offset: 0,
      size: 1000,
      sortBy: '-date',
      closedStatus: 'both',
      inOrganizationalHierarchy: true,
      'metadata-eq-reportId': entityId,
    });

    return (data?.items ?? []) as Amendment[];
  }, [entityId, orgUnitId]);

  const loading = !entityId || resourceLoading;
  const amendments = rows ?? [];
  // The backend labels the first version "Original", the newest one "Actual" and the ones it
  // supersedes "Previa" (see AmendmentCreationAction). "Original" is never relabeled, so it's the
  // live version only until a later one exists.
  const hasActual = amendments.some(row => row.metadata?.current === 'Actual');
  const isLiveVersion = (row: Amendment) =>
    row.metadata?.current === 'Actual' || (!hasActual && row.metadata?.current === 'Original');

  return (
    <div className="px-5" id={$id}>
      <FormGroup>
        <div className="worktray worktray--flat">
          <div className="worktray-flat-header">
            <div className="title">
              <h5 className="mb-0">{title}</h5>
            </div>
          </div>

          <div className="worktray-flat-scroll">
            <div className="worktray-table-container">
              {error ? (
                <Notification error={error} />
              ) : loading ? (
                <Loader dots inline size={30} />
              ) : (
                <Table striped borderless hover>
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th>{t`Id`}</th>
                      <th>{t`Version`}</th>
                      <th>{t`Actual`}</th>
                      <th>{t`Date`}</th>
                      <th>{t`Status`}</th>
                      <th aria-label={t`Actions`} />
                    </tr>
                  </thead>
                  <tbody>
                    {amendments.length ? (
                      amendments.map(row => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.metadata?.versionNumber ?? ''}</td>
                          <td>{isLiveVersion(row) ? t`Yes` : ''}</td>
                          <td>
                            {dayjs
                              .utc(row.createdAt)
                              .local()
                              .format('lll')}
                          </td>
                          <td>{amendmentStatusLabel(row.status?.[0]?.name)}</td>
                          <td className="action-buttons-cell">
                            {row.canWork ? (
                              <a className="btn btn-sm btn-primary" href={amendmentRoute(row.id)} target="_blank" rel="noopener noreferrer">
                                <Trans>Work</Trans>
                              </a>
                            ) : (
                              <a className="btn btn-sm btn-secondary" href={amendmentRoute(row.id)} target="_blank" rel="noopener noreferrer">
                                <Trans>View</Trans>
                              </a>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <EmptyState message={t`No versions found.`} size={120} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </FormGroup>
    </div>
  );
}

export default OcifReportAmendmentsList;
